import React, { useMemo, useRef, useState } from "react";
import styles from "./TrialBanner.module.css";
import modalStyles from "../modals/modal/modal.module.css";
import { tWithKeyAttr as t } from "../../i18nHelper";
import { useAppSelector } from "../../services/hooks";
import { behavioralFeatureUsageSelector } from "../../services/selectors/entitlements.selectors";

const SEVEN_DAYS_MS = 7 * 24 * 60 * 60 * 1000;
const DAY_MS = 24 * 60 * 60 * 1000;

type TrialBannerProps = {
  endsAt: string | null;
};

function formatFeatureLabel(key: string): string {
  return key
    .toLowerCase()
    .replace(/_/g, " ")
    .replace(/\b\w/g, (c) => c.toUpperCase());
}

const TrialBanner: React.FC<TrialBannerProps> = ({ endsAt }) => {
  const [dismissed, setDismissed] = useState(false);
  const [tooltipOpen, setTooltipOpen] = useState(false);
  const tooltipRef = useRef<HTMLDivElement>(null);

  const behavioralUsage = useAppSelector(behavioralFeatureUsageSelector);
  const reducedFeatures = useMemo(
    () => behavioralUsage.filter((u) => u.trialReduced),
    [behavioralUsage]
  );
  const hasReducedFeatures = reducedFeatures.length > 0;

  const { expiringSoon, days, expired } = useMemo(() => {
    if (!endsAt) {
      return { expiringSoon: false, days: 0, expired: false };
    }
    const ms = new Date(endsAt).getTime() - Date.now();
    if (ms <= 0) {
      return { expiringSoon: false, days: 0, expired: true };
    }
    return {
      expiringSoon: ms <= SEVEN_DAYS_MS,
      days: Math.ceil(ms / DAY_MS),
      expired: false,
    };
  }, [endsAt]);

  if (dismissed || expired) {
    return null;
  }

  const variantClass = expiringSoon ? styles.warning : styles.info;
  const bannerParams = { count: days, days };
  const message = expiringSoon
    ? t("TRIAL_EXPIRING_SOON_BANNER", bannerParams)
    : t("TRIAL_ACTIVE_BANNER", bannerParams);

  return (
    <div
      className={`${styles.banner} ${variantClass}`}
      role={expiringSoon ? "alert" : "status"}
    >
      <div className={styles.bannerContent}>
        <span>{message}</span>
        {hasReducedFeatures && (
          <span className={styles.secondaryLine}>
            {t("TRIAL_FEATURES_LIMITED")}{" "}
            <span className={styles.tooltipWrapper} ref={tooltipRef}>
              <button
                className={styles.seeWhatLink}
                onClick={() => setTooltipOpen((o) => !o)}
                aria-expanded={tooltipOpen}
              >
                {t("TRIAL_SEE_WHATS_LIMITED")}
              </button>
              {tooltipOpen && (
                <div className={styles.tooltip} role="tooltip">
                  <ul className={styles.featureList}>
                    {reducedFeatures.map((u) => (
                      <li key={u.feature}>{formatFeatureLabel(u.feature)}</li>
                    ))}
                  </ul>
                </div>
              )}
            </span>
          </span>
        )}
      </div>
      <button
        className={modalStyles.popupWindow__closeBtn}
        data-testid="popup-close-button"
        onClick={() => {
          setDismissed(true);
        }}
      />
    </div>
  );
};

export default TrialBanner;
